import logger from '@/lib/logger';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Barbell, FunnelSimple, Play, Info, VideoCamera, X } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BODY_PART_LABELS = {
  full_body: 'Full Body', abs: 'Abs & Core', arms: 'Arms', chest: 'Chest',
  legs: 'Legs', back: 'Back', shoulders: 'Shoulders', all: 'All'
};

const LEVEL_COLORS = {
  beginner: 'text-[#34C759] border-[#34C759]/30',
  intermediate: 'text-[#FF9500] border-[#FF9500]/30',
  advanced: 'text-[#FF3B30] border-[#FF3B30]/30',
};

export default function WorkoutLibrary() {
  const [exercises, setExercises] = useState([]);
  const [bodyPart, setBodyPart] = useState('all');
  const [level, setLevel] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [videoExercise, setVideoExercise] = useState(null);
  const navigate = useNavigate();

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (bodyPart !== 'all') params.body_part = bodyPart;
      if (level !== 'all') params.level = level;
      const { data } = await axios.get(`${API}/workouts/library`, { params });
      setExercises(data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [bodyPart, level]);

  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  const startWorkout = (exerciseList) => {
    navigate('/workouts/player', { state: { exercises: exerciseList } });
  };

  return (
    <div data-testid="workout-library-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">Exercise Library</div>
        <h1 className="text-4xl font-black tracking-tighter">WORKOUTS</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <FunnelSimple weight="bold" className="text-[#71717A] w-4 h-4" />
          <span className="label-tactical">Body Part</span>
        </div>
        {['all', 'full_body', 'abs', 'arms', 'chest', 'legs', 'back', 'shoulders'].map(bp => (
          <button key={bp} onClick={() => setBodyPart(bp)} data-testid={`filter-${bp}`}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${bodyPart === bp ? 'bg-[#FF3B30] text-white' : 'bg-[#141414] border border-white/10 text-[#A1A1AA] hover:border-white/20'}`}>
            {BODY_PART_LABELS[bp] || bp}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <div className="flex items-center gap-2">
          <FunnelSimple weight="bold" className="text-[#71717A] w-4 h-4" />
          <span className="label-tactical">Level</span>
        </div>
        {['all', 'beginner', 'intermediate', 'advanced'].map(l => (
          <button key={l} onClick={() => setLevel(l)} data-testid={`filter-level-${l}`}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${level === l ? 'bg-[#FF3B30] text-white' : 'bg-[#141414] border border-white/10 text-[#A1A1AA] hover:border-white/20'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Quick Start */}
      {exercises.length > 0 && (
        <button onClick={() => startWorkout(exercises.slice(0, 5))} className="btn-primary px-6 py-3 mb-6 flex items-center gap-2" data-testid="quick-start-button">
          <Play weight="fill" className="w-5 h-5" /> Quick Start ({Math.min(5, exercises.length)} exercises)
        </button>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {exercises.map((ex, i) => (
            <div key={ex.id || i} className="tactical-card p-5 hover:border-white/20 transition-all" data-testid={`exercise-card-${i}`}>
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold tracking-tight">{ex.name}</h3>
                <span className={`text-xs px-2 py-0.5 border font-bold uppercase ${LEVEL_COLORS[ex.level] || 'text-white border-white/20'}`}>
                  {ex.level}
                </span>
              </div>
              <div className="label-tactical text-[#FF3B30] mb-2">{BODY_PART_LABELS[ex.body_part] || ex.body_part}</div>
              <p className="text-sm text-[#A1A1AA] mb-3">{ex.description}</p>
              <div className="flex items-center gap-4 text-sm text-[#71717A] mb-3">
                <span>{ex.sets} sets</span>
                <span>{ex.reps} reps</span>
                <span>{ex.rest_seconds}s rest</span>
              </div>
              <div className="flex gap-2">
                {ex.video_url && (
                  <button onClick={() => setVideoExercise(ex)} data-testid={`video-btn-${i}`}
                    className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 border-[#007AFF]/30 text-[#007AFF] hover:bg-[#007AFF]/10">
                    <VideoCamera weight="bold" className="w-3 h-3" /> Demo
                  </button>
                )}
                <button onClick={() => setSelectedExercise(selectedExercise?.id === ex.id ? null : ex)}
                  className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1">
                  <Info weight="bold" className="w-3 h-3" /> Tips
                </button>
                <button onClick={() => startWorkout([ex])} className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1">
                  <Play weight="fill" className="w-3 h-3" /> Start
                </button>
              </div>
              {selectedExercise?.id === ex.id && (
                <div className="mt-3 p-3 bg-[#1C1C1E] border border-white/10 space-y-2">
                  <div>
                    <span className="label-tactical text-[#34C759]">Form Tip</span>
                    <p className="text-sm text-[#A1A1AA] mt-1">{ex.form_tip}</p>
                  </div>
                  <div>
                    <span className="label-tactical text-[#FF9500]">Modification</span>
                    <p className="text-sm text-[#A1A1AA] mt-1">{ex.modification}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && exercises.length === 0 && (
        <div className="text-center py-16">
          <Barbell weight="bold" className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
          <p className="text-[#71717A]">No exercises found for this filter.</p>
        </div>
      )}

      {/* Video Modal */}
      {videoExercise && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="video-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setVideoExercise(null)} />
          <div className="relative z-10 w-full max-w-2xl bg-[#141414] border border-white/10">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h3 className="text-lg font-bold tracking-tight font-['Barlow_Condensed'] uppercase">{videoExercise.name}</h3>
                <span className="text-xs text-[#71717A]">Exercise Demonstration</span>
              </div>
              <button onClick={() => setVideoExercise(null)} className="text-[#71717A] hover:text-white" data-testid="close-video-modal">
                <X weight="bold" className="w-5 h-5" />
              </button>
            </div>
            <div className="aspect-video">
              <iframe
                src={videoExercise.video_url}
                title={videoExercise.name}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="label-tactical text-[#34C759]">Form Tip</span>
                <span className="text-sm text-[#A1A1AA]">{videoExercise.form_tip}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="label-tactical text-[#FF9500]">Modification</span>
                <span className="text-sm text-[#A1A1AA]">{videoExercise.modification}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
